import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  unitSerial: string;
  unitHostname: string;
}

const ResetKeyModal: React.FC<Props> = ({ open, onClose, onConfirm, unitSerial, unitHostname }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for revoking the SSH key');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onConfirm(reason);
      handleClose();
    } catch (err) {
      setError('Failed to revoke SSH key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setError('');
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Revoke SSH Key</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" icon={<WarningIcon />}>
            <Typography variant="body2">
              You are about to revoke the SSH key for unit <strong>{unitSerial}</strong> ({unitHostname}). This action
              cannot be undone.
            </Typography>
          </Alert>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            The unit will need to re-register with a new key pair. Please provide a reason for this action:
          </Typography>
        </Box>

        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          label="Reason"
          placeholder="e.g., Security incident, key compromise, administrative reset"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError('');
          }}
          error={!!error}
          helperText={error}
          disabled={loading}
          required
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="warning" disabled={loading || !reason.trim()}>
          {loading ? 'Revoking...' : 'Revoke SSH Key'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetKeyModal;
